import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Typography } from '@one-thd/sui-atomic-components';
import { SectionHeader } from '../shared/section-header/SectionHeader';
import {
  useBusinessToolsContext,
  useCommonContext,
} from '../../hooks/useContexts';
import { BusinessToolsErrorMessage } from './BuisnessToolsErrorMessage';
import { getSvocId, getUserId } from '../../utils/user';
import { spendPercentage } from '../../utils/business-tools';
import { DateRanges } from '../../utils/DateRanges';
import { getWindow } from '../../utils/general';
import { GetTopSpendByJobName } from '../../hooks/business-tools-hooks';
import {
  IBusinessToolsRequest,
  IContextData,
  IJobSpend,
  ITopSpendByJobNameResponse,
} from '../../models/business-tools';

import '../../styles/business-tools.scss';
import { TopPOJobNameSkeleton } from './BusinessToolsSkeletons';

export interface ITopJobsState {
  showMoreJobs: number;
  hideMe: boolean;
  setHideMe: (value: boolean) => void;
  setShowMoreJobs: (value: number) => void;
}

const generateBarStyles = (percentage: number): object => {
  if (percentage <= 0.2) {
    return percentage > 0 ? { width: '0.2%' } : { width: '0%' };
  }
  return { width: `${percentage}%` };
};

// map over array and return html and value
const topJobsList = (
  jobArray: IJobSpend[],
  showMoreJobs: number,
  redirectQueryParams: IContextData,
  businessToolsHostName: string,
  navigate: ReturnType<typeof useNavigate>,
) => {
  const denominatorAmount = jobArray[0];
  return jobArray.map((job: IJobSpend, i: number) => {
    const percentageSpent = spendPercentage(
      denominatorAmount.totalSpend,
      job.totalSpend,
    );
    const barStyles = generateBarStyles(percentageSpent);
    const operator = job.totalSpend < 0 ? '-' : '';
    if (showMoreJobs === 0 && i > 4) {
      return true;
    }

    return (
      <button
        type="button"
        onClick={() => {
          getWindow()?.sessionStorage.setItem(
            'orderHistoryInfo',
            JSON.stringify({
              userId: getUserId() ?? '',
              pageSize: 1000,
            }),
          );

          navigate({
            pathname: '/myaccount/purchase-history/search-results',
            search: `?search=${encodeURIComponent(job.jobName)}`,
          });
        }}
        className="business-tools__displayBlock"
        style={{ width: '100%' }}
        key={i}
      >
        <li className="business-tools__List" key={i}>
          <div className="business-tools__Labels u__flex">
            <span>
              {job.jobName !== '' ? job.jobName : 'No Job Name Assigned'}
            </span>
            <span>{`${operator}$${Math.abs(job.totalSpend).toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
              },
            )}`}</span>
          </div>
          <div>
            <div className="business-tools__Bar" style={barStyles} />
          </div>
        </li>
      </button>
    );
  });
};

// function to load more jobs elements
const showMoreJobsOnClick = (
  showMoreJobs: number,
  setShowMoreJobs: (value: number) => void,
  setHideMe: (value: boolean) => void,
) => {
  if (showMoreJobs === 0) {
    setShowMoreJobs(showMoreJobs + 1);
    setHideMe(false);
  }
};

// function to load less jobs elements
const showLessJobsOnClick = (
  showMoreJobs: number,
  setShowMoreJobs: (value: number) => void,
  setHideMe: (value: boolean) => void,
) => {
  if (showMoreJobs === 1) {
    setShowMoreJobs(showMoreJobs - 1);
    setHideMe(true);
  }
};

const renderTopByJobsElement = (
  jobNameData: ITopSpendByJobNameResponse | null,
  jobNameError: AxiosError | null,
  jobNameLoaded: boolean,
  topJobsState: ITopJobsState,
  redirectQueryParams: IContextData,
  businessToolsHostName: string,
  navigate: ReturnType<typeof useNavigate>,
) => {
  const { showMoreJobs, hideMe, setHideMe, setShowMoreJobs } = topJobsState;
  if (jobNameLoaded) {
    /**
     * Error object if api call catch's axios error in response
     */
    if (jobNameError) {
      return BusinessToolsErrorMessage();
    }
    if (jobNameData && jobNameData.businessToolsResponse.jobsSpend.length > 0) {
      return (
        <>
          {' '}
          <div className="business-tools__fullWidth u__flex business-tools__subHeader business-tools__marginTop">
            <h4>Highest to Lowest</h4>
            <h4>Displays Pre-Tax Dollar Spend</h4>
          </div>
          <div className="jobs business-tools__marginTop business-tools__fontWeight">
            <ul>
              {topJobsList(
                jobNameData.businessToolsResponse.jobsSpend,
                showMoreJobs,
                redirectQueryParams,
                businessToolsHostName,
                navigate,
              )}
            </ul>
          </div>
          {jobNameData.businessToolsResponse.jobsSpend.length > 5 ? (
            <div className="col__6-12 col__3-12--sm business-tools__rmPaddingLeft">
              {hideMe ? (
                <button
                  type="button"
                  className="bttn-outline bttn--link"
                  id="showMoreJobsElement"
                  onClick={() =>
                    showMoreJobsOnClick(
                      showMoreJobs,
                      setShowMoreJobs,
                      setHideMe,
                    )
                  }
                >
                  <span className="bttn__content">Show more PO/Job Names</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="bttn-outline bttn--link"
                  id="showLessJobsElement"
                  onClick={() =>
                    showLessJobsOnClick(
                      showMoreJobs,
                      setShowMoreJobs,
                      setHideMe,
                    )
                  }
                >
                  <span className="bttn__content">Show less</span>
                </button>
              )}
            </div>
          ) : (
            ''
          )}
        </>
      );
    }
    return (
      <Typography weight="bold">
        No records can be found for the selected date range and/or job name.
      </Typography>
    );
  }
  return <TopPOJobNameSkeleton />;
};

export const TopJobs = () => {
  const [hideMe, setHideMe] = useState<boolean>(true);
  const [showMoreJobs, setShowMoreJobs] = useState<number>(0);
  const navigate = useNavigate();

  const { dateRangeOptions, timezone } = useCommonContext();
  const {
    dateSelectorValue,
    requestData,
    setJobSpends,
    businessToolsHostName,
  } = useBusinessToolsContext();

  const request: IBusinessToolsRequest = {
    businessToolsRequest: {
      startDate:
        dateSelectorValue === 'custom'
          ? dateRangeOptions.startDate
          : DateRanges[dateSelectorValue].startDate,
      endDate:
        dateSelectorValue === 'custom'
          ? dateRangeOptions.endDate
          : DateRanges[dateSelectorValue].endDate,
      jobName: requestData.poJobName,
      timezone,
    },
    customerAccountId: getSvocId() || '',
  };

  const topJobsState: ITopJobsState = {
    hideMe,
    showMoreJobs,
    setHideMe,
    setShowMoreJobs,
  };

  const { jobNameData, jobNameError, jobNameLoaded } = GetTopSpendByJobName(
    request,
    setJobSpends,
  );

  const redirectQueryParams: IContextData = {
    startDate:
      dateSelectorValue === 'custom'
        ? dateRangeOptions.startDate
        : DateRanges[dateSelectorValue].startDate,
    endDate:
      dateSelectorValue === 'custom'
        ? dateRangeOptions.endDate
        : DateRanges[dateSelectorValue].endDate,
    dateSelectorValue: dateSelectorValue || 'YTD',
    poJobName: requestData.poJobName || '',
  };

  return (
    <div className="top-jobs">
      <SectionHeader title="Top PO/Job Names" />
      {renderTopByJobsElement(
        jobNameData,
        jobNameError,
        jobNameLoaded,
        topJobsState,
        redirectQueryParams,
        businessToolsHostName,
        navigate,
      )}
    </div>
  );
};
